import { useState, useMemo, useEffect } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { geoMercator } from "d3-geo";
import { scaleQuantile } from "d3-scale";
import { Loader2, Info, ChevronLeft, Mic } from "lucide-react";
import { STATES_URL, DISTRICTS_URL, INDIA_CENTER, INDIA_SCALE, STATE_SCALES } from "../../utils/mapUtils";
import { normalizeGeoName, STATE_CENTERS } from "../../data/geoMappings";
import stateDistrictsMap from "../../data/stateDistrictsMap.json";
import { STATE_REACH } from "../../data/ybf/states";
import { DISTRICT_REACH } from "../../data/ybf/districts";
import { getStateInsights, getDistrictInsights, STATE_SANCTIONED, TOTAL_SANCTIONED_CR } from "../../data/ybf/stateInsights";
import { ragForScore } from "../../data/ybf/grants";
import { PILLARS } from "../../data/ybf/pillars";
import { fmtCr, fmtInt } from "../../data/ybf/utils";

// ── Lenovo RED palette — lightest darkened so low-data states are still visible ──
const PALETTE = [
  "#FECACA", "#FCA5A5", "#F87171", "#F0564B", "#EF4444",
  "#E2231A", "#C81E16", "#A81812", "#7F1D1D",
];
const NO_DATA = "#E5E7EB";    // neutral grey against the canvas (no-data states)

// ── Lookups ───────────────────────────────────────────────────────
const stateLookup = STATE_REACH.reduce((acc, s) => {
  acc[normalizeGeoName(s.state)] = s;
  return acc;
}, {});
const sanctionedLookup = STATE_SANCTIONED.reduce((acc, s) => {
  acc[normalizeGeoName(s.state)] = s.sanctionedCr;
  return acc;
}, {});
const stateColorScale = scaleQuantile()
  .domain(STATE_SANCTIONED.map((s) => s.sanctionedCr).filter((v) => v > 0))
  .range(PALETTE);

const districtLookup = {};
Object.entries(DISTRICT_REACH).forEach(([state, list]) => {
  list.forEach((d) => {
    districtLookup[normalizeGeoName(d.district)] = { ...d, state };
  });
});
const ALL_ALLOWED_DISTRICT_IDS = new Set(
  Object.keys(DISTRICT_REACH).flatMap((state) => {
    const entry = stateDistrictsMap[normalizeGeoName(state)];
    return entry?.shapeIDs || [];
  })
);
const allDistrictValues = Object.values(DISTRICT_REACH).flat().map((d) => d.beneficiaries);
const districtColorScale = scaleQuantile().domain(allDistrictValues).range(PALETTE);

export default function YBFIndiaMap() {
  const [level, setLevel] = useState("states");      // "states" | "districts"
  const [drillState, setDrillState] = useState(null); // zoomed-in state name
  const [selectedState, setSelectedState] = useState(STATE_REACH[0]?.state || null);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);

  useEffect(() => {
    setMapLoading(true);
    const t = setTimeout(() => setMapLoading(false), 500);
    return () => clearTimeout(t);
  }, [level, drillState]);

  // Effective view: drilled > level
  const view = drillState ? "drilled" : level;

  // Projection — recompute when drilling
  const projFn = useMemo(() => {
    const center = drillState ? (STATE_CENTERS[drillState] || INDIA_CENTER) : INDIA_CENTER;
    const scale  = drillState ? (STATE_SCALES[drillState]  || INDIA_SCALE * 3) : INDIA_SCALE;
    return geoMercator().center(center).scale(scale).translate([360, 240]);
  }, [drillState]);

  // Allowed district IDs for current drill state
  const drillDistrictIds = useMemo(() => {
    if (!drillState) return null;
    const entry = stateDistrictsMap[normalizeGeoName(drillState)];
    return entry ? new Set(entry.shapeIDs || []) : null;
  }, [drillState]);

  const drillDistrictData = useMemo(() => {
    if (!drillState) return null;
    const arr = DISTRICT_REACH[drillState] || [];
    const map = {};
    arr.forEach((d) => { map[normalizeGeoName(d.district)] = d; });
    const scale = arr.length
      ? scaleQuantile().domain(arr.map((d) => d.beneficiaries)).range(PALETTE)
      : null;
    return { list: arr, map, scale };
  }, [drillState]);

  const insights = useMemo(
    () => (selectedState ? getStateInsights(selectedState) : null),
    [selectedState]
  );
  const selectedKey = selectedState ? normalizeGeoName(selectedState) : null;

  return (
    <div data-testid="ybf-india-map" className="select-none">
      {/* Top bar — toggle + breadcrumb + note */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex bg-slate-100 p-0.5 rounded-lg" data-testid="map-level-toggle">
            <button
              onClick={() => { setLevel("states"); setDrillState(null); }}
              data-testid="toggle-states"
              className={`px-4 py-1 rounded-md text-[11px] font-semibold transition-all ${
                level === "states" && !drillState
                  ? "bg-white text-slate-900 shadow"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              States
            </button>
            <button
              onClick={() => { setLevel("districts"); setDrillState(null); }}
              data-testid="toggle-districts"
              className={`px-4 py-1 rounded-md text-[11px] font-semibold transition-all ${
                level === "districts" && !drillState
                  ? "bg-white text-slate-900 shadow"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Districts
            </button>
          </div>
          {drillState && (
            <button
              onClick={() => setDrillState(null)}
              data-testid="map-back-btn"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#1A1A1A] hover:text-[#E2231A] transition-colors"
            >
              <ChevronLeft size={13} /> All India
              <span className="text-slate-400 mx-1">/</span>
              <span className="text-slate-800">{drillState}</span>
              {drillDistrictData && (
                <span className="text-slate-400 ml-1 font-normal">
                  ({drillDistrictData.list.length} districts)
                </span>
              )}
            </button>
          )}
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-[10px] text-amber-800">
          <Info size={11} />
          <span>Sanctioned & disbursement for multi-state grants are proportional estimates</span>
        </div>
      </div>

      {/* Map + side panel */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div
          className="relative flex-1 rounded-xl overflow-hidden border border-slate-200"
          style={{ minHeight: 480 }}
        >
          {mapLoading && (
            <div
              className="absolute inset-0 z-40 flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.92)" }}
              data-testid="map-loading-overlay"
            >
              <div className="text-center">
                <Loader2 size={32} className="text-[#1A1A1A] animate-spin mx-auto mb-2" />
                <span className="text-sm font-medium text-slate-600">Loading…</span>
              </div>
            </div>
          )}

          <ComposableMap
            projection={projFn}
            width={820}
            height={480}
            style={{ width: "100%", height: "100%", background: "#F8FAFC" }}
            key={`${view}-${drillState || "none"}`}
          >
            {/* ── STATES level ── */}
            {view === "states" && (
              <Geographies geography={STATES_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const name = geo.properties.shapeName || "";
                    const key  = normalizeGeoName(name);
                    const sanctioned = sanctionedLookup[key] || 0;
                    const data = stateLookup[key];
                    const fill = sanctioned ? stateColorScale(sanctioned) : NO_DATA;
                    const isSelected = selectedKey === key;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => setHoveredKey(key)}
                        onMouseLeave={() => setHoveredKey(null)}
                        onClick={() => {
                          if (!data) return;
                          setSelectedState(data.state);
                          // Drill into districts if we have data
                          if (DISTRICT_REACH[data.state]) setDrillState(data.state);
                        }}
                        style={{
                          default: {
                            fill,
                            stroke: isSelected ? "#0F172A" : "#FFFFFF",
                            strokeWidth: isSelected ? 2 : 0.7,
                            outline: "none",
                            cursor: data ? "pointer" : "default",
                          },
                          hover: {
                            fill,
                            stroke: "#0F172A",
                            strokeWidth: 2,
                            outline: "none",
                            cursor: data ? "pointer" : "default",
                          },
                          pressed: { outline: "none" },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            )}

            {/* ── DISTRICTS level (all India) ── */}
            {view === "districts" && (
              <Geographies geography={DISTRICTS_URL}>
                {({ geographies }) => {
                  const visible = geographies.filter((g) =>
                    ALL_ALLOWED_DISTRICT_IDS.has(g.properties.shapeID)
                  );
                  return visible.map((geo) => {
                    const dname = geo.properties.shapeName || "";
                    const dkey  = normalizeGeoName(dname);
                    const data  = districtLookup[dkey];
                    const fill  = data ? districtColorScale(data.beneficiaries) : NO_DATA;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => setHoveredKey(dkey)}
                        onMouseLeave={() => setHoveredKey(null)}
                        onClick={() => {
                          if (data) setSelectedState(data.state);
                        }}
                        style={{
                          default: { fill, stroke: "#FFFFFF", strokeWidth: 0.4, outline: "none", cursor: data ? "pointer" : "default" },
                          hover:   { fill, stroke: "#0F172A", strokeWidth: 1.5, outline: "none" },
                          pressed: { outline: "none" },
                        }}
                      />
                    );
                  });
                }}
              </Geographies>
            )}

            {/* ── DRILLED state view (zoomed) ── */}
            {view === "drilled" && drillDistrictIds && drillDistrictData && (
              <>
                <Geographies geography={DISTRICTS_URL}>
                  {({ geographies }) =>
                    geographies
                      .filter((g) => drillDistrictIds.has(g.properties.shapeID))
                      .map((geo) => {
                        const dname = geo.properties.shapeName || "";
                        const dkey  = normalizeGeoName(dname);
                        const data  = drillDistrictData.map[dkey];
                        const fill  = data && drillDistrictData.scale
                          ? drillDistrictData.scale(data.beneficiaries)
                          : NO_DATA;
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            onMouseEnter={() => setHoveredKey(dkey)}
                            onMouseLeave={() => setHoveredKey(null)}
                            style={{
                              default: { fill, stroke: "#FFFFFF", strokeWidth: 0.6, outline: "none" },
                              hover:   { fill, stroke: "#0F172A", strokeWidth: 1.5, outline: "none" },
                              pressed: { outline: "none" },
                            }}
                          />
                        );
                      })
                  }
                </Geographies>
                {/* Navy outline overlay on top of districts */}
                <Geographies geography={STATES_URL}>
                  {({ geographies }) =>
                    geographies
                      .filter((g) => normalizeGeoName(g.properties.shapeName || "") === normalizeGeoName(drillState))
                      .map((geo) => (
                        <Geography
                          key={`outline-${geo.rsmKey}`}
                          geography={geo}
                          style={{
                            default: { fill: "none", stroke: "#1A1A1A", strokeWidth: 2, outline: "none", pointerEvents: "none" },
                            hover:   { outline: "none" },
                            pressed: { outline: "none" },
                          }}
                        />
                      ))
                  }
                </Geographies>
              </>
            )}
          </ComposableMap>

          {/* Bottom legend */}
          <div className="absolute bottom-2 left-3 right-3 flex items-center gap-2">
            <span className="text-[10px] text-slate-500 font-medium">Low</span>
            <div className="flex flex-1 h-2 rounded-full overflow-hidden border border-white/60">
              {PALETTE.map((c) => (
                <div key={c} className="flex-1 h-full" style={{ background: c }} />
              ))}
            </div>
            <span className="text-[10px] text-slate-500 font-medium">High</span>
            <span className="text-[11px] font-bold text-slate-800 ml-1">{fmtCr(TOTAL_SANCTIONED_CR)}</span>
            <span className="text-[10px] text-slate-500">sanctioned (state level)</span>
          </div>
        </div>

        {/* Side panel */}
        <SidePanel insights={insights} hoveredKey={hoveredKey} />
      </div>
    </div>
  );
}

// ── Side panel ───────────────────────────────────────────────────
function SidePanel({ insights, hoveredKey }) {
  const hoveredInsights = useMemo(() => {
    if (!hoveredKey) return null;
    // District hover takes precedence — show district-specific insights
    const distMatch = districtLookup[hoveredKey];
    if (distMatch) return getDistrictInsights(distMatch.district, distMatch.state);
    // Otherwise it's a state hover
    const stateMatch = Object.values(stateLookup).find(
      (s) => normalizeGeoName(s.state) === hoveredKey
    );
    if (stateMatch) return getStateInsights(stateMatch.state);
    return null;
  }, [hoveredKey]);

  const active = hoveredInsights || insights;
  if (!active) {
    return (
      <div className="w-full lg:w-72 flex-shrink-0 rounded-xl border border-slate-200 bg-white p-5 text-center text-xs text-slate-500">
        Click a state for details
      </div>
    );
  }

  const isDistrict = !!active.isDistrict;

  return (
    <div
      className="w-full lg:w-72 flex-shrink-0 rounded-xl border border-slate-200 bg-white p-5"
      data-testid="map-side-panel"
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        {isDistrict ? "District" : "State"}
      </div>
      <h3 className="text-xl font-bold text-slate-900 leading-tight mt-0.5">
        {isDistrict ? active.district : active.state}
      </h3>
      {isDistrict && (
        <div className="text-[11px] text-slate-500 mt-0.5">
          in <span className="font-semibold text-slate-700">{active.state}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Sanctioned</div>
          <div className="text-lg font-bold text-slate-900">{fmtCr(active.sanctionedCr)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Disbursed</div>
          <div className="text-lg font-bold text-slate-900">{fmtCr(active.disbursedCr)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Projects</div>
          <div className="text-lg font-bold text-slate-900">{active.grantsCount}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Partners</div>
          <div className="text-lg font-bold text-slate-900">{active.granteesCount}</div>
        </div>
      </div>

      {/* Avg score (RAG) + Beneficiaries reach */}
      <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Avg project score</div>
          {active.avgScore != null ? (() => {
            const rag = ragForScore(active.avgScore);
            return (
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[12px] font-bold border mt-1 ${rag.bg} ${rag.text} ${rag.border}`}
                data-testid="panel-avg-score"
                title={`${rag.label} · ${active.avgScore}/5`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: rag.dot }} />
                {active.avgScore}/5
              </span>
            );
          })() : (
            <div className="text-sm text-slate-400 mt-1">—</div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Beneficiaries reach</div>
          <div className="text-lg font-bold text-slate-900" data-testid="panel-beneficiaries">
            {fmtInt(active.beneficiaries)}
          </div>
        </div>
      </div>

      {/* Sentiment analysis */}
      {active.sentiment && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="panel-sentiment">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Sentiment Analysis
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] text-violet-700 font-semibold">
              <Mic size={10} /> {active.sentiment.samples} Voice AI samples
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200"
              data-testid="sentiment-positive"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Positive {active.sentiment.positive}%
            </span>
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200"
              data-testid="sentiment-neutral"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Neutral {active.sentiment.neutral}%
            </span>
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold bg-red-50 text-red-700 border border-red-200"
              data-testid="sentiment-negative"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              Negative {active.sentiment.negative}%
            </span>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-slate-100 space-y-1">
        {isDistrict ? (
          <div className="text-[12px] text-slate-700">
            <span className="font-bold text-slate-900">{active.blocks}</span> blocks
          </div>
        ) : (
          <>
            <div className="text-[12px] text-slate-700">
              <span className="font-bold text-slate-900">{active.districts}</span> districts
              {active.districtSampleCount > 0 && (
                <span className="text-amber-600 ml-1">({active.districtSampleCount} active)</span>
              )}
            </div>
            <div className="text-[12px] text-slate-700">
              <span className="font-bold text-slate-900">{active.blocks}</span> blocks
            </div>
          </>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Portfolios</div>
        <div className="flex flex-wrap gap-1.5">
          {PILLARS.map((p) => {
            const count = active.portfolio[p.id] || 0;
            if (count === 0) return null;
            return (
              <span
                key={p.id}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold text-white"
                style={{ background: p.color }}
                data-testid={`portfolio-chip-${p.id}`}
              >
                <span>{p.name.split(" ")[0]}</span>
                <span className="bg-white/25 rounded px-1 text-[10px]">{count}</span>
              </span>
            );
          })}
          {Object.values(active.portfolio).every((c) => c === 0) && (
            <span className="text-[11px] text-slate-400">
              {isDistrict ? "No projects in this district" : "No projects in this state"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
